import { useState, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

interface Option {
    value: string;
    label: string;
}

interface SearchableSelectProps {
    value: string | undefined;
    onValueChange: (value: string) => void;
    options: Option[];
    placeholder?: string;
    searchPlaceholder?: string;
    className?: string;
}

export function SearchableSelect({
    value,
    onValueChange,
    options,
    placeholder = 'Selecione...',
    searchPlaceholder = 'Buscar...',
    className,
}: SearchableSelectProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const selected = options.find((o) => o.value === value);

    const filtered = options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        if (open) {
            setSearch('');
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    return (
        <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        'w-full justify-between font-normal rounded-xl border-gray-200 bg-white hover:bg-gray-50',
                        !selected && 'text-gray-400',
                        className
                    )}
                >
                    <span className="truncate">{selected ? selected.label : placeholder}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                </Button>
            </PopoverTrigger>

            <PopoverContent
                className="p-0 rounded-xl border border-gray-100 shadow-xl bg-white"
                style={{ width: 'var(--radix-popover-trigger-width)', minWidth: 'var(--radix-popover-trigger-width)' }}
                align="start"
            >
                {/* Search input */}
                <div className="flex items-center border-b border-gray-100 px-3 py-2 gap-2">
                    <Search className="h-4 w-4 shrink-0 text-gray-400" />
                    <input
                        ref={inputRef}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={searchPlaceholder}
                        className="flex-1 text-sm outline-none bg-transparent placeholder:text-gray-400"
                    />
                </div>

                {/* Options list */}
                <div className="max-h-60 overflow-y-auto py-1">
                    {filtered.length === 0 ? (
                        <p className="px-4 py-6 text-center text-sm text-gray-400">
                            Nenhum resultado encontrado.
                        </p>
                    ) : (
                        filtered.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => {
                                    onValueChange(option.value);
                                    setOpen(false);
                                }}
                                className={cn(
                                    'w-full flex items-center justify-between px-4 py-2 text-sm text-left transition-colors hover:bg-gray-50',
                                    option.value === value && 'bg-blue-50 text-blue-700 font-medium'
                                )}
                            >
                                <span>{option.label}</span>
                                {option.value === value && (
                                    <Check className="h-4 w-4 text-blue-600 shrink-0" />
                                )}
                            </button>
                        ))
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
